import { useTranslation } from 'react-i18next';

// Theme-aware status colors using CSS variables for dark mode compatibility
const STATUS_MAP = {
  // Work Request statuses
  NEW: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  UNDER_REVIEW: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  CONVERTED_TO_WO: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  REJECTED: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30',
  // Work Order statuses
  OPEN: 'bg-brand/15 text-brand border-brand/30',
  ASSIGNED: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  IN_PROGRESS: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30',
  WAITING_PARTS: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  WAITING_VENDOR: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30',
  COMPLETED: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  CLOSED: 'bg-muted text-muted-foreground border-border',
};

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  const classes = STATUS_MAP[status] || 'bg-muted text-muted-foreground border-border';
  const label = t(`status.${status}`, status);
  return (
    <span
      data-testid={`status-badge-${status?.toLowerCase()}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${classes}`}
    >
      {label}
    </span>
  );
}

// Theme-aware priority colors
const PRIORITY_MAP = {
  LOW: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  MEDIUM: 'bg-brand/15 text-brand border-brand/30',
  HIGH: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30',
  CRITICAL: 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/40',
};

const PRIORITY_KEYS = {
  LOW: 'workRequest.low',
  MEDIUM: 'workRequest.medium',
  HIGH: 'workRequest.high',
  CRITICAL: 'workRequest.critical',
};

export function PriorityBadge({ priority }) {
  const { t } = useTranslation();
  const classes = PRIORITY_MAP[priority] || 'bg-muted text-muted-foreground border-border';
  const label = t(PRIORITY_KEYS[priority], priority);
  return (
    <span
      data-testid={`priority-badge-${priority?.toLowerCase()}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${classes}`}
    >
      {label}
    </span>
  );
}

// Where the maintenance came from (maintenance_source on the work order)
const SOURCE_MAP = {
  GAME_MASTER_REPORT: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30',
  TECHNICIAN_DISCOVERY: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30',
  PM_GENERATED: 'bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30',
  MANAGER_CREATED: 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-300 border-zinc-500/30',
};

export function SourceBadge({ source, compact }) {
  const { t } = useTranslation();
  const key = source && SOURCE_MAP[source] ? source : 'UNKNOWN';
  const classes = SOURCE_MAP[key] || 'bg-muted text-muted-foreground border-border';
  return (
    <span
      data-testid={`source-badge-${key.toLowerCase()}`}
      title={t(`source.${key}`)}
      className={`inline-flex items-center rounded-full border font-semibold whitespace-nowrap ${
        compact ? 'px-1.5 py-0 text-[10px]' : 'px-2.5 py-0.5 text-xs'
      } ${classes}`}
    >
      {t(`source.${key}`)}
    </span>
  );
}
